import type { Editor, NodeEntry, Path } from 'slate';
import { Range } from 'slate';

import { measureFont } from '../../measure';
import type { Length } from '../../types';
import { convertUnit, inToPx } from '../../util';
import type {
  AlineText,
  FootersElement,
  HeadersElement,
  IndentationOffsets,
  ListMarker,
  PageDimensions,
  ParagraphProps,
  RenderModelDocument,
  RenderModelFooter,
  RenderModelFooters,
  RenderModelHeader,
  RenderModelHeaders,
  RenderModelLine,
  RMContainerNode,
  RMDocProps,
} from '../internal';
import {
  applySuggestions,
  BreakVariant,
  FooterType,
  HeaderType,
  isRenderModelParagraph,
  isRenderModelTable,
  isRenderModelTableCell,
  isRMHeaderElement,
  lineDimensions,
  minimumPageContentHeightIn,
  pageSeparationPx,
  Queries,
} from '../internal';

export interface Rect {
  heightPx: number;
  leftPx: number;
  topPx: number;
  widthPx: number;
}

interface ParagraphPropsWithMarker extends ParagraphProps {
  listMarker: ListMarker | null;
  widthPx: number;
}

export interface LineOffsetDetails {
  // The correction value applied to arrive at margin left.
  correctionPx: number;
  // The aggregate margin left for the line.
  leftOffsetPx: number;
  listMarkerBufferPx: number;
  listMarkerTextWidthPx: number;
}

const dimensionsQueries = {
  /**
   * Returns the distances between the left side of a page to the edges (left
   * and right) of a paragraph. left is the distance between the left side of
   * the page and left edge of a paragraph *in inches*, right is the distance
   * between the left side of the page and right edge of a paragraph *in
   * inches*.
   */
  activeIndentationMarkerOffsets(editor: Editor): IndentationOffsets {
    const defaultOffsets: IndentationOffsets = { left: 0, right: 0 };
    if (!editor.selection) return defaultOffsets;

    const [point] = Range.edges(editor.selection);
    const rMParagraphEntry = Queries.getRMParagraphsContainingPoint(editor, { point }).next().value;
    if (!rMParagraphEntry) return defaultOffsets;

    const { leftEdge, rightEdge } = Queries.getRMBlockHorizontalEdges(
      editor,
      rMParagraphEntry,
      'in'
    );

    return { left: leftEdge, right: rightEdge };
  },

  /**
   * Returns the page content height.
   *
   * Note that this function is consumed when building the render model and therefore it has to be
   * *very* performant. In many cases, that is why Queries inside this transform do not take the
   * editor (e.g. to avoid fetching the same node - like the document - multiple times).
   */
  activePageContentHeight(
    args: {
      activePageDimensions: PageDimensions;
      pageNumber: number;
      rMDocProps: RMDocProps;
    },
    unit: Length.Unit
  ): number {
    const { activePageDimensions, pageNumber, rMDocProps } = args;

    const activeHeader = Queries.activeHeaderOnPage({
      pageNumber,
      rMDocProps,
    });
    const headerHeight = Queries.renderedHeaderOrFooterHeight(
      {
        activePageDimensions,
        rMElement: activeHeader,
        rMPart: rMDocProps.headers,
      },
      unit
    );

    const activeFooter = Queries.activeFooterOnPage({ pageNumber, rMDocProps });
    const footerHeight = Queries.renderedHeaderOrFooterHeight(
      {
        activePageDimensions,
        rMElement: activeFooter,
        rMPart: rMDocProps.footers,
      },
      unit
    );

    const marginTopHeight = Math.max(
      headerHeight,
      convertUnit(activePageDimensions.marginTop, 'in', unit)
    );
    const marginBottomHeight = Math.max(
      footerHeight,
      convertUnit(activePageDimensions.marginBottom, 'in', unit)
    );
    const contentHeight =
      convertUnit(activePageDimensions.height, 'in', unit) - marginTopHeight - marginBottomHeight;

    return Math.max(convertUnit(minimumPageContentHeightIn, 'in', unit), contentHeight);
  },

  activePageContentWidth(editor: Editor, unit: Length.Unit): number {
    const pageDimensions = Queries.activePageDimensions(editor, unit);
    return pageDimensions.width - pageDimensions.marginLeft - pageDimensions.marginRight;
  },

  activePageHeight(editor: Editor, unit: Length.Unit): number {
    return Queries.activePageDimensions(editor, unit).height;
  },

  /**
   * Returns the width of a line taking into account whether the line has a break.
   */
  adjustedLineWidthPx(
    editor: Editor,
    options: {
      paraProps: ParagraphPropsWithMarker;
      rMLine: RenderModelLine;
    }
  ): number {
    const { paraProps, rMLine } = options;
    const maxLineWidthPx = Queries.maxLineWidthPx(editor, { paraProps, rMLine });
    // If there is a page break, return maxLineWidth as the page break grows to take up all remaining line width.
    if (rMLine.contentBreak?.variant === BreakVariant.Page) return maxLineWidthPx;

    return rMLine.spaceWidthPx === 'default' ? rMLine.widthPx : maxLineWidthPx;
  },

  /**
   * Return font metrics of a text node.
   */
  fontMetrics(textNode: AlineText): { capHeightPx: number } {
    const boldActive = Queries.isBoldActive(textNode);
    const italicActive = Queries.isItalicActive(textNode);
    const fontFamily = Queries.activeFontFamily(textNode);
    const fontSizePx = Queries.renderedFontSize(textNode);
    const fontWeight = boldActive ? 'bold' : 'normal';
    const fontStyle = italicActive ? 'italic' : 'normal';
    const { capHeight } = measureFont(fontFamily, {
      fontStyle,
      fontWeight,
    });

    return { capHeightPx: capHeight * fontSizePx };
  },

  /**
   * Get the content width of the container strictly above the given content model path.
   */
  getContainerContentWidthPx(editor: Editor, options: { at: Path }): number {
    const { at } = options;
    const rMPath = Queries.getFirstRMPathFromCMPath(editor, at);
    if (!rMPath) throw new Error('Expected render model path');
    return Queries.getRMContainerContentWidthPx(editor, { rMPath });
  },

  /**
   * Get the content width of the container strictly above the given render model path.
   */
  getRMContainerContentWidthPx(editor: Editor, options: { rMPath: Path }): number {
    const { rMPath } = options;

    const tableEntry = Queries.renderModelAbove(editor, { at: rMPath, match: isRenderModelTable });
    const tableCellEntry = Queries.renderModelAbove(editor, {
      at: rMPath,
      match: isRenderModelTableCell,
    });

    if (tableEntry && tableCellEntry) {
      const [table] = tableEntry;
      const [tableCell] = tableCellEntry;
      return Queries.getRMTableCellContentWidthPx(table, tableCell);
    }

    return Queries.activePageContentWidth(editor, 'px');
  },

  getRMContainerMargins(
    editor: Editor,
    rMPath: Path,
    unit: Length.Unit
  ): { leftMargin: number; rightMargin: number } {
    const rMTableEntry = Queries.renderModelAbove(editor, {
      at: rMPath,
      match: isRenderModelTable,
    });

    const rMTableCellEntry = Queries.renderModelAbove(editor, {
      at: rMPath,
      match: isRenderModelTableCell,
    });

    if (rMTableEntry && rMTableCellEntry) {
      const [table] = rMTableEntry;
      const [tableCell] = rMTableCellEntry;
      const leftMargin = Queries.renderedTableCellMargin(table, tableCell, 'left', unit);
      const rightMargin = Queries.renderedTableCellMargin(table, tableCell, 'right', unit);
      return { leftMargin, rightMargin };
    }

    const { marginLeft: leftMargin, marginRight: rightMargin } = Queries.activePageDimensions(
      editor,
      unit
    );
    return { leftMargin, rightMargin };
  },

  /**
   * Returns the rect of the intersection between a content model selection and a specific line,
   * relative to the top of the document and the left of the page.
   */
  getRangeLineIntersectionRectInDocument(
    editor: Editor,
    cMRange: Range,
    rMLineEntry: NodeEntry<RenderModelLine>
  ): Rect | null {
    const [rMLine, rMLinePath] = rMLineEntry;

    const rMParaEntry = Queries.renderModelAbove(editor, {
      at: rMLinePath,
      match: isRenderModelParagraph,
    });
    if (!rMParaEntry) return null;
    const [rMPara, rMParaPath] = rMParaEntry;

    const pageNum = Queries.getPageNumberForRMPath(editor, rMParaPath);
    if (pageNum === null) return null;

    const cMParaPath = Queries.getCMPathFromRMPath(editor, rMParaPath);

    const cMLineRange = Queries.getLineCMRange(cMParaPath, rMLine);
    const cMRangeIntersection = Range.intersection(cMRange, cMLineRange);
    if (!cMRangeIntersection) return null;

    const { xLeft: lineLeftPx, yTop: lineTopPx } = Queries.getRMNodeAbsolutePosition(
      editor,
      rMLineEntry,
      'px'
    );

    const { from, to } = lineDimensions(editor, {
      cMParaPath,
      rMLine,
      rMPara,
      spanOptions: {
        from: cMRangeIntersection.anchor,
        to: cMRangeIntersection.focus,
      },
    });

    return {
      heightPx: rMLine.baselinePx + rMLine.maxDescentPx,
      leftPx: lineLeftPx + from,
      topPx: lineTopPx + Queries.getTopOfPagePx(editor, pageNum),
      widthPx: to - from,
    };
  },

  getTopOfPagePx(editor: Editor, pageNum: number): number {
    const pageHeight = Queries.activePageHeight(editor, 'px');
    return (pageNum - 1) * pageHeight + pageNum * pageSeparationPx;
  },

  /**
   * Returns the applicable header's offset bottom, *in pixels*.
   */
  headerOffsetBottomPx(args: {
    activePageDimensions: PageDimensions;
    rMHeader: RenderModelHeader;
    rMHeadersPart: RenderModelHeaders;
  }): number {
    const { activePageDimensions, rMHeader, rMHeadersPart } = args;
    const { heightPx: contentHeightPx } = rMHeader;
    const headerHeightPx = Queries.renderedHeaderOrFooterHeight(
      {
        activePageDimensions,
        rMElement: rMHeader,
        rMPart: rMHeadersPart,
      },
      'px'
    );
    const headerMarginPx = inToPx(rMHeadersPart.headerMargin);
    const offsetBottomPx = headerHeightPx - (contentHeightPx + headerMarginPx);

    return Math.max(0, offsetBottomPx);
  },

  /**
   * Utility function for determining the amount a line should be offset (e.g., marginLeft) in a paragraph,
   * taking into account paragraph alignment, first-line indent, AND leading spaces.  For instance, in a right-aligned paragraph
   * we make sure the last non-space character is flush against the right side of the paragraph edge and render all leading spaces after.
   * This is consistent with the behavior of Google Docs and Word (but results in some known weird UI states if a user presses space at the end
   * of a line a bunch of times).
   */
  lineOffsetLeftPx(
    editor: Editor,
    options: {
      paraProps: ParagraphPropsWithMarker;
      rMLine: Omit<RenderModelLine, 'spaceWidthPx'>;
    }
  ): LineOffsetDetails {
    const { paraProps, rMLine } = options;
    const { firstLineIndentAdjustedValPx, listMarkerBufferPx, listMarkerTextWidthPx } =
      Queries.adjustedFirstLineIndentPx(editor, {
        listMarker: paraProps.listMarker,
        paragraph: paraProps,
      });
    const paragraphAlignment = Queries.renderedParagraphAlignment(paraProps);
    const maxLineWidth = rMLine.isFirstLine
      ? paraProps.widthPx - firstLineIndentAdjustedValPx
      : paraProps.widthPx;
    const lineWidthMinusLeadingSpaces = rMLine.widthPx - rMLine.leadingSpacesWidthPx;

    // Returns the left margin.
    switch (paragraphAlignment) {
      case 'center': {
        const centerCorrection = (maxLineWidth - lineWidthMinusLeadingSpaces) / 2;

        return rMLine.isFirstLine
          ? {
              correctionPx: centerCorrection,
              leftOffsetPx: firstLineIndentAdjustedValPx + centerCorrection,
              listMarkerBufferPx,
              listMarkerTextWidthPx,
            }
          : {
              correctionPx: centerCorrection,
              leftOffsetPx: centerCorrection,
              listMarkerBufferPx,
              listMarkerTextWidthPx,
            };
      }
      case 'right': {
        const rightCorrection = maxLineWidth - lineWidthMinusLeadingSpaces;
        return rMLine.isFirstLine
          ? {
              correctionPx: rightCorrection,
              leftOffsetPx: firstLineIndentAdjustedValPx + rightCorrection,
              listMarkerBufferPx,
              listMarkerTextWidthPx,
            }
          : {
              correctionPx: rightCorrection,
              leftOffsetPx: rightCorrection,
              listMarkerBufferPx,
              listMarkerTextWidthPx,
            };
      }
      case 'left':
      default: {
        return rMLine.isFirstLine
          ? {
              correctionPx: 0,
              leftOffsetPx: firstLineIndentAdjustedValPx,
              listMarkerBufferPx,
              listMarkerTextWidthPx,
            }
          : { correctionPx: 0, leftOffsetPx: 0, listMarkerBufferPx, listMarkerTextWidthPx };
      }
    }
  },

  maxHeaderOrFooterHeight(activePageDimensions: PageDimensions, unit: Length.Unit): number {
    const { height } = activePageDimensions;
    return convertUnit((height - minimumPageContentHeightIn) / 2, 'in', unit);
  },

  /**
   * Returns the maximum width of a given line.  This is the width of the max amount of content a line can hold vs. the width
   * of content in a line, which is held on rMLine.widthPx.
   */
  maxLineWidthPx(
    editor: Editor,
    options: {
      paraProps: ParagraphPropsWithMarker;
      rMLine: Pick<RenderModelLine, 'isFirstLine'>;
    }
  ): number {
    const { paraProps, rMLine } = options;
    const { firstLineIndentAdjustedValPx } = Queries.adjustedFirstLineIndentPx(editor, {
      listMarker: paraProps.listMarker,
      paragraph: paraProps,
    });

    if (!rMLine.isFirstLine) return paraProps.widthPx;

    return paraProps.widthPx - firstLineIndentAdjustedValPx;
  },

  /**
   * Returns the amount of space/width that is remaining after the line's content, not accounting
   * for justified text.
   */
  nonJustifiedRemainingLineWidthPx(
    editor: Editor,
    options: { paraProps: ParagraphPropsWithMarker; rMLine: Omit<RenderModelLine, 'spaceWidthPx'> }
  ): number {
    const { paraProps, rMLine } = options;
    const maxLineWidthPx = Queries.maxLineWidthPx(editor, { paraProps, rMLine });
    return maxLineWidthPx - rMLine.widthPx;
  },

  offsetLeftPx({ activePageDimensions }: { activePageDimensions: PageDimensions }): number {
    return inToPx(activePageDimensions.marginLeft);
  },

  /**
   * Calculate the offset top *in pixels* for where *content first appears
   * relative* to a parent rMContainerNode. Used for resolving the vertical
   * position of a click.
   */
  offsetTopPx(args: {
    activePageDimensions: PageDimensions;
    rMContainerNode: RMContainerNode;
    rMDoc: RenderModelDocument;
  }): number {
    const { activePageDimensions, rMContainerNode, rMDoc } = args;

    switch (rMContainerNode.type) {
      case 'page': {
        const activeHeader = Queries.activeHeaderOnPage({
          pageNumber: rMContainerNode.pageNum,
          rMDocProps: rMDoc,
        });
        const activeHeaderHeightPx = Queries.renderedHeaderOrFooterHeight(
          {
            activePageDimensions,
            rMElement: activeHeader,
            rMPart: rMDoc.headers,
          },
          'px'
        );

        return Math.max(0, activeHeaderHeightPx);
      }
      case FooterType.Default:
      case FooterType.FirstPage:
      case FooterType.Even: {
        const { heightPx: footerContentHeightPx } = rMContainerNode;
        const activeFooterHeightPx = Queries.renderedHeaderOrFooterHeight(
          {
            activePageDimensions,
            rMElement: rMContainerNode,
            rMPart: rMDoc.footers,
          },
          'px'
        );
        const activeFooterMarginPx = Queries.renderedHeaderOrFooterMargin(rMDoc.footers, 'px');
        const offsetPx = activeFooterHeightPx - (activeFooterMarginPx + footerContentHeightPx);

        return Math.max(0, offsetPx);
      }
      case HeaderType.Default:
      case HeaderType.FirstPage:
      case HeaderType.Even: {
        return Queries.renderedHeaderOrFooterMargin(rMDoc.headers, 'px');
      }
    }
  },

  renderedHeaderOrFooterHeight(
    options: {
      activePageDimensions: PageDimensions;
      rMElement: RenderModelHeader | RenderModelFooter;
      rMPart: RenderModelHeaders | RenderModelFooters;
    },
    unit: Length.Unit
  ): number {
    const { activePageDimensions, rMElement, rMPart } = options;
    const { marginBottom: marginBottomIn, marginTop: marginTopIn } = activePageDimensions;
    const isHeader = isRMHeaderElement(rMElement);

    const maxHeight = Queries.maxHeaderOrFooterHeight(activePageDimensions, unit);
    const minHeight = convertUnit(isHeader ? marginTopIn : marginBottomIn, 'in', unit);

    const margin = Queries.renderedHeaderOrFooterMargin(rMPart, unit);
    const contentHeight = convertUnit(rMElement.heightPx, 'px', unit) + margin;
    const adjustedContentHeight = Math.min(maxHeight, contentHeight);

    return Math.max(minHeight, adjustedContentHeight);
  },

  /**
   * Returns the active header or footer margin, taking into account suggested edits.
   */
  renderedHeaderOrFooterMargin(
    partElement: Omit<HeadersElement | FootersElement, 'children'>,
    unit: Length.Unit
  ): number {
    const partWithEdits = applySuggestions(partElement);
    const marginIn =
      'headerMargin' in partWithEdits
        ? partWithEdits.headerMargin
        : 'footerMargin' in partWithEdits
          ? partWithEdits.footerMargin
          : 0.5;
    return convertUnit(Math.max(0, Number(marginIn)), 'in', unit);
  },

  textFontMetrics(
    textNode: AlineText,
    { withOriginalFontSize = false }: { withOriginalFontSize?: boolean } = {}
  ) {
    const boldActive = Queries.isBoldActive(textNode);
    const italicActive = Queries.isItalicActive(textNode);
    const fontFamily = Queries.activeFontFamily(textNode);
    const fontSizePx = withOriginalFontSize
      ? Queries.activeFontSizePx(textNode)
      : Queries.renderedFontSize(textNode);
    const fontWeight = boldActive ? 'bold' : 'normal';
    const fontStyle = italicActive ? 'italic' : 'normal';
    const metrics = measureFont(fontFamily, {
      fontStyle,
      fontWeight,
      origin: 'baseline',
    });
    const { ascent, descent } = metrics;

    // measure func returns negative values for certain metrics if it's measuring relative to baseline.
    const ascentPositive = -ascent;
    const textDescentPx = descent * fontSizePx;
    /**
     * Give the text a little bit of top padding in the line.  Note this is not some scientific measurement, just based
     * on a lot of manual testing w/ individual fonts.  Maybe in the future we can figure out the "proper" measurement
     * based on font, etc.
     *
     * Increased from 0.1 to 0.2 to accommodate accented capital letters (Á, É, etc.) which extend higher
     * than the standard ascent measurement and were getting clipped at the top of canvas lines.
     */
    const defaultAscentToTop = 0.2;
    const textBaselineToTopPx = ascentPositive * fontSizePx;
    const textBaselineToTopWithPaddingPx = (ascentPositive + defaultAscentToTop) * fontSizePx;

    return { metrics, textBaselineToTopPx, textBaselineToTopWithPaddingPx, textDescentPx };
  },
};

export default dimensionsQueries;

/**
 * Compute the smallest rect that contains the given pair of rects.
 */
export function combineRects(a: Rect, b: Rect): Rect {
  const topPx = Math.min(a.topPx, b.topPx);
  const leftPx = Math.min(a.leftPx, b.leftPx);
  const bottomPx = Math.max(a.topPx + a.heightPx, b.topPx + b.heightPx);
  const rightPx = Math.max(a.leftPx + a.widthPx, b.leftPx + b.widthPx);
  return { heightPx: bottomPx - topPx, leftPx, topPx, widthPx: rightPx - leftPx };
}
